import type { ReactNode } from 'react';
import { Account, Schedule } from '@/features/accounts/types';
import { type ScheduleControls } from '@/features/accounts/schedule/hooks/useScheduleControls';
import ScheduleList from './ScheduleList';
import SchedulesLoadingState from './SchedulesLoadingState';
import SchedulesErrorState from './SchedulesErrorState';
import RemoveAccountButton from './RemoveAccountButton';

interface ScheduleState {
  workable: Schedule[];
  rest: Schedule[];
  isLoading: boolean;
  error: string | null;
}

interface Props {
  account: Account;
  scheduleState: ScheduleState;
  scheduleActions: ScheduleControls;
  onRemove: () => void;
  isRemoving: boolean;
  editPasswordAction: ReactNode;
}

export default function AccountCardView({ account, scheduleState, scheduleActions, onRemove, isRemoving, editPasswordAction }: Props) {
  return (
    <div className="relative flex h-full flex-col rounded-2xl border border-slate-200 bg-white/90 shadow-xl shadow-slate-200/60">
      <div className="px-5 pt-5">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1">
          <div className="col-start-2 row-start-1 flex items-center justify-self-end">
            {editPasswordAction}
            <RemoveAccountButton
              onClick={onRemove}
              isDisabled={isRemoving}
              className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <span
            className="block max-w-[240px] overflow-hidden text-ellipsis whitespace-nowrap text-base font-semibold text-slate-900"
            title={account.email}
          >
            {account.email}
          </span>
          <span className="text-sm text-slate-500">
            {account.country_code} → {account.to_country_code}
          </span>
        </div>
      </div>
      <div className="mt-4 flex-1 rounded-b-2xl border-t border-slate-200 bg-slate-50/80">
        {scheduleState.isLoading && <SchedulesLoadingState />}
        {scheduleState.error && <SchedulesErrorState message={scheduleState.error} />}
        {!scheduleState.isLoading && !scheduleState.error && (
          <ScheduleList
            workable={scheduleState.workable}
            rest={scheduleState.rest}
            actions={scheduleActions}
          />
        )}
      </div>
    </div>
  );
}
